import { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { DealImage } from "../../../types/dealImages";
import DealImageTypeChip from "./DealImageTypeChip";

const VISIBLE_COUNT = 4;
const GAP_PX = 16;
const SLOT_ASPECT = 3 / 4;
const MAX_SLOT_HEIGHT = 320;

function buildCarouselImages(
  hero: DealImage | null,
  images: DealImage[],
): DealImage[] {
  if (!hero) return images;
  return [hero, ...images.filter((img) => img.id !== hero.id)];
}

const arrowSx = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#0E4664",
  backgroundColor: "rgba(255,255,255,0.9)",
  boxShadow: 1,
  transition: "opacity 0.2s, background-color 0.2s",
  "&:hover": { backgroundColor: "#fff" },
  "&.Mui-disabled": { color: "rgba(14, 70, 100, 0.35)" },
} as const;

interface DealImageCarouselProps {
  hero?: DealImage | null;
  images: DealImage[];
  onImageError?: () => void;
  onClick?: () => void;
}

export default function DealImageCarousel({
  hero = null,
  images,
  onImageError,
  onClick,
}: DealImageCarouselProps) {
  const allImages = useMemo(
    () => buildCarouselImages(hero, images),
    [hero, images],
  );
  const [startIndex, setStartIndex] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [slotWidth, setSlotWidth] = useState(0);

  const visibleCount = Math.min(VISIBLE_COUNT, allImages.length);
  const maxStartIndex = Math.max(0, allImages.length - VISIBLE_COUNT);
  const hasPages = maxStartIndex > 0;

  useEffect(() => {
    setStartIndex((i) => Math.min(i, maxStartIndex));
  }, [maxStartIndex]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const update = () => {
      const totalGap = (visibleCount - 1) * GAP_PX;
      setSlotWidth((viewport.clientWidth - totalGap) / visibleCount);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [visibleCount]);

  if (allImages.length === 0) return null;

  const slotHeight = slotWidth
    ? Math.min(slotWidth * SLOT_ASPECT, MAX_SLOT_HEIGHT)
    : undefined;
  const slideOffset = startIndex * (slotWidth + GAP_PX);

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Box
        onClick={onClick}
        sx={{
          position: "relative",
          width: "100%",
          cursor: onClick ? "pointer" : "default",
        }}
      >
        <Box
          ref={viewportRef}
          sx={{ overflow: "hidden", width: "100%", pb: 1, maxHeight: MAX_SLOT_HEIGHT }}
        >
          <Box
            sx={{
              display: "flex",
              gap: `${GAP_PX}px`,
              transform: slotWidth ? `translateX(-${slideOffset}px)` : undefined,
              transition: "transform 0.35s ease",
            }}
          >
            {allImages.map((image) => (
              <Box
                key={image.id}
                sx={{
                  position: "relative",
                  flexShrink: 0,
                  width: slotWidth || undefined,
                  height: slotHeight || undefined,
                  maxHeight: MAX_SLOT_HEIGHT,
                  aspectRatio: slotWidth ? undefined : "4/3",
                  flex: slotWidth ? undefined : `0 0 calc((100% - ${(visibleCount - 1) * GAP_PX}px) / ${visibleCount})`,
                  borderRadius: "12px",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  boxShadow:
                    "0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.06)",
                  transition: "box-shadow 0.25s ease",
                  "&:hover": {
                    boxShadow:
                      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.08)",
                    "& .carousel-image-overlay": { opacity: 1 },
                    "& img": { transform: "scale(1.05)" },
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={image.url}
                    alt=""
                    onError={onImageError}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      transition: "transform 0.25s ease",
                    }}
                  />
                </Box>
                <Box
                  className="carousel-image-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0,
                    pointerEvents: "none",
                    transition: "opacity 0.25s ease",
                    background:
                      "linear-gradient(to top, rgba(17, 24, 39, 0.18), rgba(17, 24, 39, 0.04) 45%, transparent 70%)",
                  }}
                />
                <DealImageTypeChip
                  imageType={image.image_type}
                  sx={{ position: "absolute", bottom: 10, left: 10, zIndex: 1 }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {hasPages && (
          <>
            <IconButton
              size="small"
              aria-label="Previous images"
              disabled={startIndex === 0}
              onClick={(e) => {
                e.stopPropagation();
                setStartIndex((i) => i - 1);
              }}
              sx={{ ...arrowSx, left: 12, opacity: startIndex > 0 ? 0.9 : 0.35 }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Next images"
              disabled={startIndex >= maxStartIndex}
              onClick={(e) => {
                e.stopPropagation();
                setStartIndex((i) => i + 1);
              }}
              sx={{
                ...arrowSx,
                right: 12,
                opacity: startIndex < maxStartIndex ? 0.9 : 0.35,
              }}
            >
              <ChevronRightIcon />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 10,
                right: 14,
                color: "text.secondary",
                pointerEvents: "none",
              }}
            >
              {startIndex + 1}–
              {Math.min(startIndex + VISIBLE_COUNT, allImages.length)} of{" "}
              {allImages.length}
            </Typography>
          </>
        )}
      </Box>

      {hasPages && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 1 }}>
          {Array.from({ length: maxStartIndex + 1 }, (_, page) => (
            <Box
              key={page}
              component="button"
              type="button"
              aria-label={`Go to page ${page + 1}`}
              onClick={() => setStartIndex(page)}
              sx={{
                width: page === startIndex ? 20 : 8,
                height: 8,
                p: 0,
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                backgroundColor: page === startIndex ? "#0E4664" : "#c5c5c5",
                transition: "width 0.2s, background-color 0.2s",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
